import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import "../../../../styles/useCustomScrollbar";
import { calculateCPFRatio, formatCPFRatio } from "../../CPFCalculator";
import {
  getAllDishesMealPlan,
  autocompleteDishName,
  createMealPlan,
  updateMealPlan,
} from "../../../../../services/mealPlans";
import {
  createGlobalTemplateMealOptions,
  updateGlobalTemplateMealOptions,
} from "../../../../../services/globalTemplates";
import {
  addAdhocMealPlanOption,
  updateAdhocMealPlanOption,
} from "../../../../../services/adhocMealPlans";
import { ReactComponent as DeleteIcon } from "../../../../../assects/svg/client_details/delete.svg";
import CustomDropdown, { DropdownOption } from "../../../../Ui_Components/CustomDropdown";
import useCustomScrollbar from "../../../../styles/useCustomScrollbar";
import { Button } from "../../../../Ui_Components/Button";

export interface DishItem {
  id: string;
  name: string;
  quantity: string;
  unit: string;
  oil: number;
  cal: number;
  gl: number;
  carb: number;
  protein: number;
  fat: number;
  is_hidden?: boolean;
}

export interface CookingFat {
  id: number;
  min_qty: string;
  max_qty: string | null;
  cooking_fat_value: string;
}

export interface Measurement {
  measurement_id: number;
  fk_unit_id: number;
  unit_type: string;
  measure_gram: string;
  measure_unit_type: string;
  measure_type: string;
}

export interface AvailableDish {
  dish_id: number;
  name: string;
  tags: string[];
  measurements: Measurement[];
  units: string[];
  unitMeasurements: { [key: string]: number };
  baseQuantity: string;
  defaultUnit: string;
  nutritionPer100g: {
    oil: number;
    cal: number;
    gl: number;
    carb: number;
    protein: number;
    fat: number;
    ingredientsFat: number;
  };
  is_dynamic_fat: boolean;
  cooking_fats: CookingFat[];
}

// ─── Collision-safe ID generator ─────────────────────────────────────────────
let dishCounter = 0;
const generateDishId = (): string => `dish_${Date.now()}_${++dishCounter}`;

const emptyDish = (): DishItem => ({
  id: generateDishId(),
  name: "",
  quantity: "",
  unit: "",
  oil: 0,
  cal: 0,
  gl: 0,
  carb: 0,
  protein: 0,
  fat: 0,
});

/** Transform raw API dish data into AvailableDish shape */
const transformDish = (dish: any): AvailableDish => {
  const units = dish.measurements.map((m: Measurement) => m.unit_type);
  const unitMeasurements: { [key: string]: number } = {};
  dish.measurements.forEach((m: Measurement) => {
    unitMeasurements[m.unit_type] = parseFloat(m.measure_gram);
  });
  return {
    dish_id: dish.dish_id,
    name: dish.dish_name,
    tags: dish.tags || [],
    measurements: dish.measurements,
    units,
    unitMeasurements,
    baseQuantity: dish.dish_base_quantity
      ? String(parseFloat(dish.dish_base_quantity))
      : "100",
    defaultUnit: dish.dish_units || "gm",
    nutritionPer100g: {
      oil: parseFloat(dish.dish_oil || "0"),
      cal: parseFloat(dish.dish_cals || "0"),
      gl: parseFloat(dish.dish_gl || "0"),
      carb: parseFloat(dish.dish_carbs || "0"),
      protein: parseFloat(dish.dish_AAFAdj_protein || "0"),
      fat: parseFloat(dish.dish_total_fat || "0"),
      ingredientsFat: parseFloat(dish.dish_ingredients_fat || "0"),
    },
    is_dynamic_fat: dish.is_dynamic_fat || false,
    cooking_fats: dish.cooking_fats || [],
  };
};

// ─── DishRow ──────────────────────────────────────────────────────────────────


interface DishRowProps {
  dish: DishItem;
  // The parent's full unfiltered cache — used to resolve already-selected dishes
  allDishesCache: React.MutableRefObject<AvailableDish[]>;
  userId: string | number;
  onUpdate: (id: string, field: keyof DishItem, value: any) => void;
  onRemove: (id: string) => void;
  onClearRow: (id: string) => void;
  totalRows: number;
  onShowToast?: (type: "success" | "error" | "warning", title: string, message?: string) => void;
}

const DishRow: React.FC<DishRowProps> = ({
  dish,
  allDishesCache,
  userId,
  onUpdate,
  onRemove,
  onClearRow,
  totalRows,
  onShowToast,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [inputValue, setInputValue] = useState(dish.name);
  // Used solely to trigger a re-render after cache is populated for pre-loaded dishes
  const [, setUnitRefresh] = useState(0);
  // Dropdown items from GET_ALL_DISHES_MEAL_PLAN (full dish data)
  const [dropdownItems, setDropdownItems] = useState<AvailableDish[]>([]);
  // Ghost suggestion string from AUTOCOMPLETE_DISH_NAME
  const [ghostSuggestion, setGhostSuggestion] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingDish, setIsFetchingDish] = useState(false);

  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync local input when parent clears or resets the row
  useEffect(() => {
    setInputValue(dish.name);
  }, [dish.name]);

  // Recalculate fixed position whenever the dropdown opens
  useEffect(() => {
    if (isDropdownOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
  }, [isDropdownOpen]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  // When a pre-loaded dish (edit mode) is not in the shared cache, fetch it so
  // the unit dropdown can populate. The ref update alone won't re-render, so
  // setUnitRefresh forces one after the cache is filled.
  useEffect(() => {
    if (!dish.name || dish.is_hidden) return;
    if (allDishesCache.current.find((d) => d.name === dish.name)) return;

    let cancelled = false;
    getAllDishesMealPlan(userId, dish.name)
      .then((data) => {
        if (cancelled) return;
        if (data.success === 1 && data.dishes?.length > 0) {
          const transformed: AvailableDish[] = data.dishes.map(transformDish);
          const existingIds = new Set(allDishesCache.current.map((d) => d.dish_id));
          allDishesCache.current = [
            ...allDishesCache.current,
            ...transformed.filter((d) => !existingIds.has(d.dish_id)),
          ];
          setUnitRefresh((v) => v + 1);
        }
      })
      .catch(console.error);

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dish.name, dish.is_hidden]);

  // Fire both APIs in parallel on every keystroke
  const fetchBoth = useCallback(
    (query: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();

      if (!query.trim()) {
        setDropdownItems([]);
        setGhostSuggestion("");
        setIsDropdownOpen(false);
        return;
      }

      debounceRef.current = setTimeout(async () => {
        const ctrl = new AbortController();
        abortRef.current = ctrl;
        setIsLoading(true);
        try {
          const [dishData, acData] = await Promise.all([
            getAllDishesMealPlan(userId, query.trim()),
            autocompleteDishName(query.trim()),
          ]);
          if (ctrl.signal.aborted) return;

          // ── Dropdown — GET_ALL_DISHES_MEAL_PLAN ──────────────────────────────
          if (dishData.success === 1 && dishData.dishes?.length > 0) {
            const transformed: AvailableDish[] = dishData.dishes.map(transformDish);
            setDropdownItems(transformed);
            setIsDropdownOpen(true);
            setHighlightedIndex(-1);
            // Merge into shared cache so nutrition resolves on selection
            const existingIds = new Set(allDishesCache.current.map((d) => d.dish_id));
            allDishesCache.current = [
              ...allDishesCache.current,
              ...transformed.filter((d) => !existingIds.has(d.dish_id)),
            ];
          } else {
            setDropdownItems([]);
            setIsDropdownOpen(false);
          }

          // ── Ghost text — AUTOCOMPLETE_DISH_NAME ──────────────────────────────
          if (acData.success === 1 && acData.suggestions?.length > 0) {
            setGhostSuggestion(acData.suggestions[0].dish_name);
          } else {
            setGhostSuggestion("");
          }
        } catch (err) {
          if (err instanceof Error && err.name === "AbortError") return;
          setDropdownItems([]);
          setGhostSuggestion("");
          setIsDropdownOpen(false);
        } finally {
          if (!ctrl.signal.aborted) setIsLoading(false);
        }
      }, 200);
    },
    [userId, allDishesCache]
  );

  // Select a dish from the dropdown (full data already in cache from fetchBoth)
  const selectFromDropdown = useCallback(
    (selected: AvailableDish) => {
      setIsDropdownOpen(false);
      setDropdownItems([]);
      setGhostSuggestion("");
      setHighlightedIndex(-1);
      setInputValue(selected.name);
      onUpdate(dish.id, "name", selected.name);
    },
    [dish.id, onUpdate]
  );

  // Accept the inline ghost suggestion (may need a data fetch if not in cache)
  const acceptGhost = useCallback(async () => {
    if (!ghostSuggestion) return;
    const name = ghostSuggestion;
    setIsDropdownOpen(false);
    setDropdownItems([]);
    setGhostSuggestion("");
    setInputValue(name);

    const cached = allDishesCache.current.find((d) => d.name === name);
    if (cached) {
      onUpdate(dish.id, "name", name);
      return;
    }

    setIsFetchingDish(true);
    try {
      const data = await getAllDishesMealPlan(userId, name);
      if (data.success === 1 && data.dishes?.length > 0) {
        const transformed: AvailableDish[] = data.dishes.map(transformDish);
        const existingIds = new Set(allDishesCache.current.map((d) => d.dish_id));
        allDishesCache.current = [
          ...allDishesCache.current,
          ...transformed.filter((d) => !existingIds.has(d.dish_id)),
        ];
      }
    } catch (err) {
      console.error("Error fetching dish details:", err);
      onShowToast?.("error", "Failed to load dish details", "Please try again.");
    } finally {
      setIsFetchingDish(false);
    }
    onUpdate(dish.id, "name", name);
  }, [ghostSuggestion, dish.id, userId, allDishesCache, onUpdate, onShowToast]);

  // Ghost text: the part of the autocomplete suggestion after what's already typed
  const ghostText =
    ghostSuggestion && inputValue && ghostSuggestion.toLowerCase().startsWith(inputValue.toLowerCase())
      ? ghostSuggestion.slice(inputValue.length)
      : "";

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    fetchBoth(val);
    if (!val) onClearRow(dish.id);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, dropdownItems.length - 1));
      setIsDropdownOpen(true);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && isDropdownOpen && dropdownItems.length > 0) {
      e.preventDefault();
      const idx = highlightedIndex >= 0 ? highlightedIndex : 0;
      if (dropdownItems[idx]) selectFromDropdown(dropdownItems[idx]);
    } else if ((e.key === "Tab" || e.key === "ArrowRight") && ghostText) {
      e.preventDefault();
      acceptGhost();
    } else if (e.key === "Escape") {
      setIsDropdownOpen(false);
      setGhostSuggestion("");
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      setIsDropdownOpen(false);
      setGhostSuggestion("");
      if (inputValue !== dish.name) setInputValue(dish.name);
    }, 150);
  };

  const isRowEmpty = totalRows === 1 && !dish.name && !dish.quantity && !dish.unit;

  const selectedDish = allDishesCache.current.find((d) => d.name === dish.name);
  const availableUnits = selectedDish?.units || [];
  const unitOptions: DropdownOption[] = availableUnits.map((unit) => {
    const measurement = selectedDish?.measurements.find((m) => m.unit_type === unit);
    const label = measurement ? `${unit} - ${measurement.measure_gram}gm` : unit;
    return { value: unit, label };
  });

  const handleDeleteClick = () => {
    if (isRowEmpty) return;
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    onRemove(dish.id);
    setShowDeleteConfirm(false);
  };

  if (showDeleteConfirm) {
    return (
      <div className="flex items-center justify-between mb-2 px-3 py-[6px] bg-[#FFF5F5] border border-[#FFCCC9] rounded-md">
        <span className="text-sm text-[#1F1F1F] truncate mr-4">
          Delete <span className="font-medium">"{dish.name}"</span>?
        </span>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="danger"
            size="xs"
            onClick={handleConfirmDelete}
            className="whitespace-nowrap"
          >
            Confirm delete
          </Button>
          <button
            onClick={() => setShowDeleteConfirm(false)}
            className="text-[#8E8E93] hover:text-[#1F1F1F] transition-colors p-1 rounded border-[0.5px] border-[#1F1F1F]"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  if (dish.is_hidden) {
    return (
      <div className="grid grid-cols-[3fr_2.6fr_0.8fr_0.5fr_0.5fr_0.5fr_0.5fr_0.6fr_0.5fr_0.5fr] gap-3 mb-2 items-center">
        <div className="px-2 py-2 border border-[#FFCDD2] bg-[#FFF5F5] rounded-md text-sm text-[#1F1F1F] truncate">
          {dish.name}
        </div>
        <div className="text-sm text-[#FF6B35] font-medium">Hidden won't be added</div>
        {[...Array(7)].map((_, i) => (
          <div key={i} className="text-sm text-center text-[#C7C7CC]">-</div>
        ))}
        <div className="flex justify-center items-center">
          <button
            onClick={() => onRemove(dish.id)}
            className="text-[#FF3B30] hover:text-red-700 cursor-pointer transition-colors"
          >
            <DeleteIcon className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-[3fr_2.6fr_0.8fr_0.5fr_0.5fr_0.5fr_0.5fr_0.6fr_0.5fr_0.5fr] gap-3 mb-2 items-center">
      {/* Dish Name — dropdown from GET_ALL_DISHES_MEAL_PLAN + ghost text from AUTOCOMPLETE_DISH_NAME */}
      <div className="relative w-full">
        {/* Ghost text layer behind the input */}
        {ghostText && (
          <div
            className="absolute inset-0 flex items-center px-2 pointer-events-none overflow-hidden text-sm"
            aria-hidden="true"
          >
            <span className="invisible whitespace-pre">{inputValue}</span>
            <span className="text-[#C7C7CC] whitespace-pre">{ghostText}</span>
          </div>
        )}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={isFetchingDish ? "Loading dish…" : isLoading ? "Searching…" : "Search dish"}
          disabled={isFetchingDish}
          autoComplete="off"
          className="w-full px-2 py-2 border border-[#E5E5EA] rounded-md text-sm outline-none focus:border-[#0DB5B5] bg-transparent relative z-[1] disabled:opacity-60 disabled:cursor-wait"
        />
        {/* Dropdown from GET_ALL_DISHES_MEAL_PLAN — rendered via portal to escape overflow clipping */}
        {isDropdownOpen && dropdownItems.length > 0 && dropdownPos &&
          ReactDOM.createPortal(
            <div
              style={{ position: "fixed", top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width, zIndex: 9999 }}
              className="bg-white border border-[#E5E5EA] rounded-md shadow-lg max-h-[200px] overflow-y-auto"
            >
              {dropdownItems.map((d, i) => (
                <div
                  key={d.dish_id}
                  onMouseDown={() => selectFromDropdown(d)}
                  className={`px-3 py-2 cursor-pointer text-sm transition-colors ${
                    i === highlightedIndex
                      ? "bg-[#F0FAFA] text-[#0DB5B5]"
                      : "text-[#1F1F1F] hover:bg-[#F5F5F5]"
                  }`}
                >
                  <div className="font-medium truncate">{d.name}</div>
                  {d.tags.length > 0 && (
                    <div className="text-xs text-[#8E8E93] truncate">{d.tags.join(", ")}</div>
                  )}
                </div>
              ))}
            </div>,
            document.body
          )}
      </div>

      {/* Unit */}
      <CustomDropdown
        options={unitOptions}
        value={dish.unit}
        onChange={(value) => onUpdate(dish.id, "unit", value)}
        placeholder="Select unit"
        disabled={!dish.name}
        searchable={false}
        maxHeight="160px"
        zIndex={60}
      />

      {/* Quantity */}
      <input
        type="text"
        value={dish.quantity}
        onChange={(e) => {
          const value = e.target.value;
          if (value === "" || /^\d*\.?\d*$/.test(value)) {
            onUpdate(dish.id, "quantity", value);
          }
        }}
        onKeyDown={(e) => {
          const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "."];
          if (!allowedKeys.includes(e.key) && !/^\d$/.test(e.key)) {
            e.preventDefault();
          }
          if (e.key === "." && dish.quantity.includes(".")) {
            e.preventDefault();
          }
        }}
        placeholder="Quantity"
        className="w-full px-2 py-2 border border-[#E5E5EA] rounded-md text-sm outline-none focus:border-[#0DB5B5]"
      />

      {/* Nutrition values */}
      <div className="text-sm text-center text-[#1F1F1F]"></div>
      <div className="text-sm text-center text-[#1F1F1F]">{dish.cal.toFixed(0).padStart(2, "0")}</div>
      <div className="text-sm text-center text-[#1F1F1F]">{dish.gl.toFixed(0).padStart(2, "0")}</div>
      <div className="text-sm text-center text-[#1F1F1F]">{dish.carb.toFixed(0).padStart(2, "0")}</div>
      <div className="text-sm text-center text-[#1F1F1F]">{dish.protein.toFixed(0).padStart(2, "0")}</div>
      <div className="text-sm text-center text-[#1F1F1F]">{dish.fat.toFixed(0).padStart(2, "0")}</div>

      {/* Delete */}
      <div className="flex justify-center items-center">
        <button
          onClick={handleDeleteClick}
          disabled={isRowEmpty}
          title={isRowEmpty ? "Nothing to delete" : "Delete row"}
          className={`transition-colors ${
            isRowEmpty
              ? "text-[#D1D1D6] cursor-not-allowed"
              : "text-[#FF3B30] hover:text-red-700 cursor-pointer"
          }`}
        >
          <DeleteIcon className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>
      </div>
    </div>
  );
};

// ─── Types ────────────────────────────────────────────────────────────────────

export interface EditingOptionData {
  id: string;
  name: string;
  dishes: DishItem[];
  instructions?: string;
  optionNumber?: number;
  totals: {
    cal: number;
    carb: number;
    fat: number;
    protein: number;
    gl: number;
  };
}

interface CreateDishModalProps {
  isOpen: boolean;
  onClose: () => void;
  mealType: string;
  userMealId: number;
  userId: string | number;
  optionNumber: number;
  onCreateOption: (
    optionName: string,
    dishes: DishItem[],
    instructions: string,
    totals: { cal: number; carb: number; fat: number; protein: number; gl: number }
  ) => void;
  editMode?: boolean;
  editingOptionData?: EditingOptionData;
  onUpdateOption?: (
    optionId: string,
    optionName: string,
    dishes: DishItem[],
    instructions: string,
    totals: { cal: number; carb: number; fat: number; protein: number; gl: number }
  ) => void;
  onShowToast?: (type: "success" | "error" | "warning", title: string, message?: string) => void;
  isGlobalTemplateMode?: boolean;
  templateId?: string | number;
  isAdhocMealMode?: boolean;
  adhocPlanId?: string | number;
}

// ─── CreateDishModal ──────────────────────────────────────────────────────────

const CreateDishModal: React.FC<CreateDishModalProps> = ({
  isOpen,
  onClose,
  mealType,
  userMealId,
  userId,
  optionNumber,
  onCreateOption,
  editMode = false,
  editingOptionData,
  onUpdateOption,
  onShowToast,
  isGlobalTemplateMode = false,
  templateId,
  isAdhocMealMode = false,
  adhocPlanId,
}) => {
  useCustomScrollbar();
  const [optionName, setOptionName] = useState("");
  const [nameError, setNameError] = useState("");
  const [dishes, setDishes] = useState<DishItem[]>([emptyDish()]);
  const [instructions, setInstructions] = useState("");
  const [showInstructions, setShowInstructions] = useState(false);
  const [showDeleteInstructionsConfirm, setShowDeleteInstructionsConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const optionNameInputRef = useRef<HTMLInputElement>(null);
  const fetchAbortRef = useRef<AbortController | null>(null);
  // Shared full-list cache — rows read from this ref, never setState through it
  const allDishesCache = useRef<AvailableDish[]>([]);

  // ─── Initial fetch on open ────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;

    const ctrl = new AbortController();
    fetchAbortRef.current = ctrl;

    (async () => {
      try {
        const data = await getAllDishesMealPlan(userId, "");
        if (ctrl.signal.aborted) return;

        if (data.success === 1 && data.dishes) {
          const transformed: AvailableDish[] = data.dishes.map(transformDish);
          allDishesCache.current = transformed;
        } else {
          allDishesCache.current = [];
        }
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") return;
        console.error("Error fetching dishes:", error);
        onShowToast?.("error", "Failed to load dishes", "Please try refreshing the page");
      }
    })();

    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userId]);

  // ─── Cleanup on unmount ───────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (fetchAbortRef.current) fetchAbortRef.current.abort();
    };
  }, []);

  // ─── Reset form when modal opens / edit data changes ─────────────────────
  useEffect(() => {
    if (!isOpen) return;

    if (editMode && editingOptionData) {
      setOptionName(editingOptionData.name);
      setDishes(
        editingOptionData.dishes.length > 0
          ? editingOptionData.dishes.map((d) => ({ ...d }))
          : [emptyDish()]
      );
      if (editingOptionData.instructions?.trim()) {
        setInstructions(editingOptionData.instructions);
        setShowInstructions(true);
      } else {
        setInstructions("");
        setShowInstructions(false);
      }
    } else if (!editMode) {
      setOptionName("");
      setDishes([emptyDish()]);
      setInstructions("");
      setShowInstructions(false);
    }
    setNameError("");
    setShowDeleteInstructionsConfirm(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editMode, editingOptionData?.id]);

  // ─── Auto-focus option name ───────────────────────────────────────────────
  useEffect(() => {
    if (isOpen && optionNameInputRef.current) {
      const timer = setTimeout(() => {
        optionNameInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setOptionName("");
    setNameError("");
    setDishes([emptyDish()]);
    setInstructions("");
    setShowInstructions(false);
    setShowDeleteInstructionsConfirm(false);
    onClose();
  }, [onClose]);

  const addDish = () => {
    setDishes((prev) => [...prev, emptyDish()]);
  };

  const getCookingFatValue = (actualQuantity: number, cookingFats: CookingFat[]): number => {
    if (!cookingFats || cookingFats.length === 0) return 0;
    for (const cf of cookingFats) {
      const minQty = parseFloat(cf.min_qty);
      const maxQty = cf.max_qty ? parseFloat(cf.max_qty) : null;
      const value = parseFloat(cf.cooking_fat_value);
      if (maxQty === null) {
        if (actualQuantity >= minQty) return value;
      } else {
        if (actualQuantity >= minQty && actualQuantity <= maxQty) return value;
      }
    }
    return 0;
  };

  const computeNutrition = (
    selectedDish: AvailableDish,
    quantity: string,
    unit: string
  ): Partial<DishItem> => {
    const quantityValue = parseFloat(quantity);
    const measurementValue = selectedDish.unitMeasurements[unit] || 100;

    if (isNaN(quantityValue) || !unit) {
      return { oil: 0, cal: 0, gl: 0, carb: 0, protein: 0, fat: 0 };
    }

    const actualQuantity = quantityValue * measurementValue;
    const multiplier = actualQuantity / 100.0;

    const oil = selectedDish.nutritionPer100g.oil * multiplier;
    const gl = selectedDish.nutritionPer100g.gl * multiplier;
    const carb = selectedDish.nutritionPer100g.carb * multiplier;
    const protein = selectedDish.nutritionPer100g.protein * multiplier;

    let fat: number;
    let cal: number;

    if (selectedDish.is_dynamic_fat && selectedDish.nutritionPer100g.ingredientsFat > 0) {
      const scaledIngredientsFat = selectedDish.nutritionPer100g.ingredientsFat * multiplier;
      const cookingFatValue = getCookingFatValue(actualQuantity, selectedDish.cooking_fats);
      fat = scaledIngredientsFat + cookingFatValue;
      cal = selectedDish.nutritionPer100g.cal * multiplier + cookingFatValue * 9;
    } else {
      const effectiveFat =
        selectedDish.nutritionPer100g.fat > 0
          ? selectedDish.nutritionPer100g.fat
          : selectedDish.nutritionPer100g.ingredientsFat;
      fat = effectiveFat * multiplier;
      cal = selectedDish.nutritionPer100g.cal * multiplier;
    }

    return { oil, cal, gl, carb, protein, fat };
  };

  const updateDish = (id: string, field: keyof DishItem, value: any) => {
    setDishes((prev) =>
      prev.map((dish) => {
        if (dish.id !== id) return dish;

        if (field === "name") {
          // Look up from full cache so selection always works regardless of what
          // other rows have searched for
          const selectedDish = allDishesCache.current.find((d) => d.name === value);
          if (!selectedDish) {
            return { ...dish, name: value, quantity: "", unit: "", oil: 0, cal: 0, gl: 0, carb: 0, protein: 0, fat: 0 };
          }
          const autoQty = selectedDish.baseQuantity;
          const autoUnit = selectedDish.defaultUnit;
          const nutrition = computeNutrition(selectedDish, autoQty, autoUnit);
          return { ...dish, name: value, quantity: autoQty, unit: autoUnit, ...nutrition };
        }

        if (field === "unit") {
          return { ...dish, unit: value, quantity: "", oil: 0, cal: 0, gl: 0, carb: 0, protein: 0, fat: 0 };
        }

        if (field === "quantity") {
          const updatedDish = { ...dish, quantity: value };
          const selectedDish = allDishesCache.current.find((d) => d.name === updatedDish.name);
          if (selectedDish && updatedDish.unit && value !== "") {
            const nutrition = computeNutrition(selectedDish, value, updatedDish.unit);
            return { ...updatedDish, ...nutrition };
          }
          return { ...updatedDish, oil: 0, cal: 0, gl: 0, carb: 0, protein: 0, fat: 0 };
        }

        return { ...dish, [field]: value };
      })
    );
  };

  const removeDish = (id: string) => {
    setDishes((prev) => {
      const remaining = prev.filter((d) => d.id !== id);
      return remaining.length === 0 ? [emptyDish()] : remaining;
    });
  };

  const clearDishRow = (id: string) => {
    setDishes((prev) =>
      prev.map((dish) =>
        dish.id === id
          ? { ...dish, name: "", quantity: "", unit: "", oil: 0, cal: 0, gl: 0, carb: 0, protein: 0, fat: 0 }
          : dish
      )
    );
  };

  const calculateTotals = () =>
    dishes.reduce(
      (acc, dish) => ({
        cal: acc.cal + dish.cal,
        carb: acc.carb + dish.carb,
        fat: acc.fat + dish.fat,
        protein: acc.protein + dish.protein,
        gl: acc.gl + dish.gl,
      }),
      { cal: 0, carb: 0, fat: 0, protein: 0, gl: 0 }
    );

  const totals = calculateTotals();
  const cpfResult = calculateCPFRatio(totals.carb, totals.protein, totals.fat, totals.cal);
  const cpfRatio = formatCPFRatio(cpfResult.ratio);

  const filledDishes = dishes.filter((d) => d.name && d.quantity && d.unit);
  const hasAnyEntry = dishes.some((d) => d.name || d.quantity || d.unit);
  const hasPartialDish = dishes.some((d) => {
    const count = [!!d.name, !!d.quantity, !!d.unit].filter(Boolean).length;
    return count > 0 && count < 3;
  });
  const isSubmitDisabled = !optionName.trim() || filledDishes.length === 0 || hasPartialDish;

  const handleSubmit = async () => {
    const trimmedName = optionName.trim();
    const currentEditingData = editingOptionData;
    const currentFilledDishes = dishes.filter((d) => d.name && d.quantity && d.unit);
    const currentTotals = calculateTotals();
    const currentInstructions = instructions;

    if (!trimmedName) {
      if (hasAnyEntry || editMode) {
        setNameError("Please enter an option name");
        optionNameInputRef.current?.focus();
      }
      return;
    }

    if (currentFilledDishes.length === 0) {
      onShowToast?.("warning", "Add dishes", "Please add at least one dish with name, quantity, and unit");
      return;
    }

    try {
      setIsSubmitting(true);

      const foodItems = currentFilledDishes
        .map((dish) => {
          const selectedDish = allDishesCache.current.find((d) => d.name === dish.name);
          if (!selectedDish) return null;
          const measurement = selectedDish.measurements.find((m) => m.unit_type === dish.unit);
          if (!measurement) return null;
          return {
            dish_id: selectedDish.dish_id,
            quantity: parseFloat(dish.quantity),
            units: dish.unit,
            measurement_unit_id: measurement.measurement_id,
          };
        })
        .filter((item): item is NonNullable<typeof item> => item !== null);

      if (foodItems.length === 0) {
        onShowToast?.("error", "Invalid dishes", "Unable to process dishes. Please check your selections.");
        setIsSubmitting(false);
        return;
      }

      if (editMode && currentEditingData && onUpdateOption) {
        const optionId = parseInt(currentEditingData.id, 10);
        if (isNaN(optionId) || optionId <= 0) {
          onShowToast?.(
            "error",
            "Invalid option",
            "Could not identify the meal option to update. Please close and reopen the editor."
          );
          setIsSubmitting(false);
          return;
        }

        let data: any;
        if (isGlobalTemplateMode && templateId) {
          const globalFoodItems = currentFilledDishes.map((dish) => {
            const selectedDish = allDishesCache.current.find((d) => d.name === dish.name);
            if (!selectedDish) return null;
            const measurement = selectedDish.measurements.find((m) => m.unit_type === dish.unit);
            if (!measurement) return null;
            const foodId = parseInt(dish.id, 10);
            return {
              ...(foodId > 0 ? { food_id: foodId } : {}),
              dish_id: selectedDish.dish_id,
              quantity: parseFloat(dish.quantity),
              units: dish.unit,
              measurement_unit_id: measurement.measurement_id,
            };
          }).filter((item): item is NonNullable<typeof item> => item !== null);

          const payload = {
            option_id: optionId,
            fk_template_meal: userMealId,
            option_no: currentEditingData.optionNumber ?? 1,
            option_name: trimmedName,
            option_instructions: currentInstructions.trim() || "",
            food_items: globalFoodItems,
          };
          data = await updateGlobalTemplateMealOptions(templateId, payload);
        } else if (isAdhocMealMode && adhocPlanId) {
          // Existing foods carry an adhoc_food_id (from the details response);
          // newly-added rows omit it so the backend creates them.
          const adhocFoodItems = currentFilledDishes.map((dish) => {
            const selectedDish = allDishesCache.current.find((d) => d.name === dish.name);
            if (!selectedDish) return null;
            const measurement = selectedDish.measurements.find((m) => m.unit_type === dish.unit);
            if (!measurement) return null;
            const adhocFoodId = parseInt(dish.id, 10);
            return {
              ...(adhocFoodId > 0 ? { adhoc_food_id: adhocFoodId } : {}),
              dish_id: selectedDish.dish_id,
              quantity: parseFloat(dish.quantity),
              units: dish.unit,
              measurement_unit_id: measurement.measurement_id,
            };
          }).filter((item): item is NonNullable<typeof item> => item !== null);

          const payload = {
            option_id: optionId,
            fk_adhoc_meal: userMealId,
            option_no: currentEditingData.optionNumber ?? 1,
            option_name: trimmedName,
            option_instructions: currentInstructions.trim() || "",
            food_items: adhocFoodItems,
          };
          data = await updateAdhocMealPlanOption(adhocPlanId, payload);
        } else {
          const payload = {
            fk_user_meal: userMealId,
            option_id: optionId,
            option_no: currentEditingData.optionNumber ?? 1,
            option_name: trimmedName,
            option_instructions: currentInstructions.trim() || "",
            food_items: foodItems,
          };
          data = await updateMealPlan(payload);
        }

        const isSuccess = data.success === true || data.success === "1" || data.success === 1;

        if (isSuccess) {
          onUpdateOption(
            currentEditingData.id,
            trimmedName,
            currentFilledDishes,
            currentInstructions,
            currentTotals
          );
          onShowToast?.("success", "Meal option updated", data.data?.message || data.message || "Your meal option has been successfully updated");
          handleClose();
        } else {
          onShowToast?.("error", "Update failed", data.message || "Failed to update meal option. Please try again.");
        }
      } else {
        let data: any;
        if (isGlobalTemplateMode && templateId) {
          const payload = {
            fk_template_meal: userMealId,
            option_no: optionNumber,
            option_name: trimmedName,
            option_instructions: currentInstructions.trim() || "",
            food_items: foodItems,
          };
          data = await createGlobalTemplateMealOptions(templateId, payload);
        } else if (isAdhocMealMode && adhocPlanId) {
          const payload = {
            fk_adhoc_meal: userMealId,
            option_no: optionNumber,
            option_name: trimmedName,
            option_instructions: currentInstructions.trim() || "",
            food_items: foodItems,
          };
          data = await addAdhocMealPlanOption(adhocPlanId, payload);
        } else {
          const payload = {
            fk_user_meal: userMealId,
            option_no: optionNumber,
            option_name: trimmedName,
            option_instructions: currentInstructions.trim() || "",
            food_items: foodItems,
          };
          data = await createMealPlan(payload);
        }

        const isSuccess = data.success === true || data.success === "1" || data.success === 1;

        if (isSuccess) {
          onCreateOption(trimmedName, currentFilledDishes, currentInstructions, currentTotals);
          onShowToast?.("success", "Meal option created", data.message || "Your meal option has been successfully added");
          handleClose();
        } else {
          onShowToast?.("error", "Creation failed", data.message || "Failed to create meal option. Please try again.");
        }
      }
    } catch (error) {
      console.error("Error submitting meal option:", error);
      onShowToast?.("error", "Something went wrong", "An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const filledDishesCount = dishes.filter((d) => d.name || d.quantity).length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-md w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex-1 overflow-y-auto p-6">
          {/* Header */}
          <div className="mb-4 border-b-[0.5px] border-[#E5E5EA] pb-3">
            <div className="flex items-center justify-between">
              <input
                ref={optionNameInputRef}
                type="text"
                value={optionName}
                onChange={(e) => {
                  setOptionName(e.target.value);
                  if (nameError) setNameError("");
                }}
                placeholder="Type your option name"
                className={`flex-1 text-[18px] font-medium text-[#1F1F1F] placeholder-[#C7C7CC] border-none outline-none ${
                  nameError ? "placeholder-[#FF3B30]" : ""
                }`}
              />
              <span className="text-[#8E8E93] text-sm ml-4 whitespace-nowrap">
                {mealType} –{" "}
                <span className="text-[#1F1F1F] font-medium">{filledDishesCount}</span>
              </span>
            </div>
            {nameError && (
              <p className="mt-1 text-xs text-[#FF3B30] flex items-center gap-1">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <circle cx="6" cy="6" r="5.5" stroke="#FF3B30" />
                  <path d="M6 4v2.5M6 8v.5" stroke="#FF3B30" strokeWidth="1.2" strokeLinecap="round" />
                </svg>
                {nameError}
              </p>
            )}
          </div>

          {/* Totals bar */}
          <div className="flex items-center justify-between gap-8 mb-4 border-[0.5px] border-[#E5E5EA] rounded-md px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#1F1F1F]">Cal -</span>
              <span className="text-sm font-medium text-[#B44AE6]">{Math.round(totals.cal)}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#1F1F1F]">Carb -</span>
              <span className="text-sm font-medium text-[#FFB532]">{Math.round(totals.carb)}g</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#1F1F1F]">Fats -</span>
              <span className="text-sm font-medium text-[#F25BEC]">{Math.round(totals.fat)}g</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#1F1F1F]">A.pro -</span>
              <span className="text-sm font-medium text-[#6BBDFA]">{Math.round(totals.protein)}g</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#1F1F1F]">GL -</span>
              <span className="text-sm font-medium text-[#A0CA07]">{Math.round(totals.gl)}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#1F1F1F]">C:P:F</span>
              <span className="text-sm font-medium text-[#007AFF]">{cpfRatio}</span>
            </div>
          </div>

          {/* Dish table */}
          <div className="mb-4 h-[250px] bg-[#FAFAFA] p-4 rounded-md overflow-y-auto custom-scrollbar-overlay pr-2">
            {/* Column headers */}
            <div className="grid grid-cols-[3fr_2.6fr_0.8fr_0.5fr_0.5fr_0.5fr_0.5fr_0.6fr_0.5fr_0.5fr] gap-3 mb-3 text-[12px] 3xl:text-[14px] text-[#646464] font-inter font-medium border-b-[0.5px] border-[#E5E5EA] pb-3">
              <div>Name</div>
              <div>Units</div>
              <div>Quantity</div>
              <div className="text-center"></div>
              <div className="text-center">Cal</div>
              <div className="text-center">GL</div>
              <div className="text-center">Carb</div>
              <div className="text-center">Protein</div>
              <div className="text-center">Fat</div>
              <div></div>
            </div>

            {dishes.map((dish) => (
              <DishRow
                key={dish.id}
                dish={dish}
                allDishesCache={allDishesCache}
                userId={userId}
                onUpdate={updateDish}
                onRemove={removeDish}
                onClearRow={clearDishRow}
                totalRows={dishes.length}
                onShowToast={onShowToast}
              />
            ))}

            <button
              onClick={addDish}
              className="flex items-center gap-1.5 text-[#0DB5B5] text-sm font-medium hover:text-[#0CA0A0] transition-colors mt-3"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M8 3.5V12.5M3.5 8H12.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
              Add dish
            </button>
          </div>

          {/* Instructions */}
          {!showInstructions ? (
            <button
              onClick={() => setShowInstructions(true)}
              className="flex items-center gap-2 text-[#0DB5B5] text-sm font-medium hover:text-[#0CA0A0] transition-colors"
            >
              <span className="text-xl">+</span>
              Add instructions
              <span className="text-xs text-[#8D8D8D] font-normal">(Optional)</span>
            </button>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-[#1F1F1F]">
                  Add instructions{" "}
                  <span className="text-xs text-[#8D8D8D] font-normal">(Optional)</span>
                </label>

                {showDeleteInstructionsConfirm ? (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[#1F1F1F]">
                      Delete instructions?
                    </span>
                    <Button
                      variant="danger"
                      size="xs"
                      onClick={() => {
                        setShowInstructions(false);
                        setInstructions("");
                        setShowDeleteInstructionsConfirm(false);
                      }}
                      className="whitespace-nowrap"
                    >
                      Confirm delete
                    </Button>
                    <button
                      onClick={() => setShowDeleteInstructionsConfirm(false)}
                      className="text-[#8E8E93] hover:text-[#1F1F1F] transition-colors p-0.5 rounded border-[0.5px] border-[#1F1F1F]"
                    >
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path
                          d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowDeleteInstructionsConfirm(true)}
                    className="text-[#FF3B30] hover:text-[#E63228] transition-colors"
                  >
                    <DeleteIcon className="w-5 h-5" />
                  </button>
                )}
              </div>
              <textarea
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="e.g., Blend all ingredients until smooth, serve chilled"
                className="w-full px-4 py-3 border border-[#E0E0E0] rounded-md text-sm font-inter focus:outline-none focus:border-[#0DB5B5] resize-none"
                rows={4}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-[#E5E5EA] p-4 flex justify-end gap-3">
          <Button
            variant="secondary"
            size="md"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={handleSubmit}
            disabled={isSubmitting || isSubmitDisabled}
          >
            {isSubmitting ? (
              <>
                <svg
                  className="animate-spin h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                {editMode ? "Updating..." : "Creating..."}
              </>
            ) : editMode ? (
              "Save & update"
            ) : (
              "Create"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateDishModal;